/**
 * EL TABLÓN DE «BUSCO PARTIDO».
 *
 * Javier: «publicar retos donde pueda seleccionar la fecha, la gente puede
 * seleccionar el lugar, y las categorías que quieran retar, uno toca el
 * whatsapp con los datos de publicación y comienza a conversar, el que crea el
 * reto lo puede borrar».
 *
 * ES UN AVISO, NO UN RETO: no crea un partido ni suma puntos. Sirve para
 * encontrarse; lo que pase después se arregla por WhatsApp o retándose por
 * ranking dentro del club, que es el camino que ya existe.
 *
 * EL TELÉFONO NO ESTÁ EN LA LISTA. La respuesta del servidor no lo trae: se
 * pide de a uno, al tocar «escribirle». Es lo único que separa este tablón
 * —que ve cualquiera con cuenta— de una guía telefónica que se descarga sola
 * con solo abrir la pantalla.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { CategoryLevel, ChallengePost, SaveChallengePostRequest } from '@tennisup/contracts';
import { CATEGORY_LEVELS, CHALLENGE_POST_LIMITS } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { Avatar } from '../components/Avatar';
import { Field } from '../components/Field';
import { api, assetUrl } from '../lib/api';
import { errorMessage, errorSummary } from '../lib/errorMessage';
import { whatsappHref } from '../lib/whatsapp';
import { useAnotarVisita } from '../hooks/useAnotarVisita';

interface Formulario {
  playsOn: string;
  placeText: string;
  levels: CategoryLevel[];
  note: string;
}

const VACÍO: Formulario = { playsOn: '', placeText: '', levels: [], note: '' };

export function ChallengePostsPage(): JSX.Element {
  // El globito del inicio baja al entrar acá. Ver `useAnotarVisita`.
  useAnotarVisita('posts');
  const { t } = useTranslation();
  const [avisos, setAvisos] = useState<ChallengePost[]>([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** `null` = el formulario está cerrado. Un aviso = se está editando ése. */
  const [editando, setEditando] = useState<ChallengePost | 'nuevo' | null>(null);

  /*
   * LOS FILTROS. Javier: «recuerda hacer filtros en busco partido si se hacen
   * muchos», más «un check de Retos para Hoy, y otro Retos para Mañana».
   *
   * EL DÍA SE GUARDA COMO EL ATAJO ELEGIDO Y NO COMO UNA FECHA, y esa es la
   * decisión que hace que esto siga andando a medianoche: guardando la fecha,
   * quien deja el tablón abierto de noche se queda con el «hoy» de ayer
   * marcado, mostrando avisos vencidos bajo un rótulo que dice hoy. Guardando
   * «HOY», la fecha se calcula al pedir y siempre es la de ahora.
   */
  const [niveles, setNiveles] = useState<CategoryLevel[]>([]);
  const [cuando, setCuando] = useState<'TODO' | 'HOY' | 'MAÑANA'>('TODO');

  /** ¿Hay algo puesto? Decide si «no hay nada» se explica o se disculpa. */
  const hayFiltro = niveles.length > 0 || cuando !== 'TODO';
  const limpiar = (): void => {
    setNiveles([]);
    setCuando('TODO');
  };

  const buscar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const pagina = await api.listPosts(0, { levels: niveles, ...rangoDe(cuando) });
      setAvisos(pagina.items);
      setTotal(pagina.total);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setCargando(false);
    }
  }, [t, niveles, cuando]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  async function borrar(aviso: ChallengePost): Promise<void> {
    setError(null);
    try {
      await api.removePost(aviso.id);
      await buscar();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /**
   * «YA CONSEGUÍ PARTIDO» — y lo contrario, para deshacerlo.
   *
   * NO BORRA NADA: el aviso se queda una semana más, marcado, con el botón de
   * escribirle puesto. Javier: «la idea es que la gente pueda contactarlo para
   * un futuro reto, sabemos que es una persona activa para jugar».
   */
  async function concretar(aviso: ChallengePost, ya: boolean): Promise<void> {
    setError(null);
    try {
      await api.setPostSettled(aviso.id, ya);
      await buscar();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  if (editando !== null) {
    return (
      <PostForm
        aviso={editando === 'nuevo' ? null : editando}
        onDone={() => {
          setEditando(null);
          void buscar();
        }}
        onCancel={() => setEditando(null)}
      />
    );
  }

  return (
    <AppLayout>
      {/* AL INICIO: «Busco partido» se abre desde los accesos rápidos, que
          es la tarjeta resaltada de la pantalla de inicio. */}
      <BackButton to="/" />
      <h1>{t('posts.title')}</h1>
      <p className="help">{t('posts.lead')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <button type="button" onClick={() => setEditando('nuevo')}>
        {t('posts.add')}
      </button>

      {/*
        LOS FILTROS VAN ANTES DE LA LISTA Y DESPUÉS DE PUBLICAR.

        Antes de la lista porque son lo que la explica: ver «3 avisos» sin ver
        que hay un filtro puesto es creer que el tablón está vacío. Después del
        botón de publicar porque publicar es a lo que más gente viene.
      */}
      <Filtros
        niveles={niveles}
        cuando={cuando}
        onNiveles={setNiveles}
        onCuando={setCuando}
      />

      {cargando ? (
        <p className="help">{t('common.loading')}</p>
      ) : avisos.length === 0 ? (
        /*
          VACÍO POR FILTRO Y VACÍO DE VERDAD SON DOS COSAS.

          «Todavía no hay avisos» delante de un filtro puesto es mentira, y de
          las que hacen que alguien se vaya creyendo que el tablón no sirve.
          Con filtro, el texto lo dice y ofrece quitarlo.
        */
        hayFiltro ? (
          <p className="help">
            {t('posts.emptyFiltered')}{' '}
            <button type="button" className="link" onClick={limpiar}>
              {t('posts.clearFilters')}
            </button>
          </p>
        ) : (
          <p className="help">{t('posts.empty')}</p>
        )
      ) : (
        <>
          <p className="help">{t('posts.count', { count: total })}</p>
          <ul className="post-list">
            {avisos.map((aviso) => (
              <PostCard
                key={aviso.id}
                post={aviso}
                onEdit={() => setEditando(aviso)}
                onDelete={() => void borrar(aviso)}
                onSettle={(ya) => void concretar(aviso, ya)}
              />
            ))}
          </ul>
        </>
      )}
    </AppLayout>
  );
}

function PostCard({
  post,
  onEdit,
  onDelete,
  onSettle,
}: {
  post: ChallengePost;
  onEdit: () => void;
  onDelete: () => void;
  onSettle: (ya: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [pidiendo, setPidiendo] = useState(false);
  /** El enlace, si el navegador bloqueó la ventana y hay que ofrecerlo a mano. */
  const [enlace, setEnlace] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  /**
   * PIDE EL NÚMERO Y RECIÉN AHÍ ABRE WHATSAPP.
   *
   * LA VENTANA SE ABRE ANTES DE PEDIR EL NÚMERO, vacía, y se le pone la
   * dirección cuando llega. No es un rodeo: un `window.open` DESPUÉS de un
   * `await` ya no cuenta como «lo pidió la persona» y los navegadores lo
   * bloquean —en el teléfono, en silencio—. Abrirla dentro del clic y
   * llenarla después es lo que hace que el botón funcione a la primera.
   *
   * SI IGUAL LA BLOQUEARON, no se pierde nada: el enlace queda a la vista para
   * tocarlo. Un botón que no hace nada y no dice nada es peor que dos toques.
   */
  async function escribir(): Promise<void> {
    setPidiendo(true);
    setAviso(null);
    const ventana = window.open('', '_blank');
    try {
      const { phone } = await api.contactoDeAviso(post.id);
      const href = phone === null ? null : whatsappHref(phone, mensaje(t, post));
      if (href === null) {
        ventana?.close();
        setAviso(t('posts.noPhone'));
        return;
      }
      if (ventana === null) setEnlace(href);
      else ventana.location.href = href;
    } catch {
      ventana?.close();
      setAviso(t('posts.contactFailed'));
    } finally {
      setPidiendo(false);
    }
  }

  const concretado = post.settledAt !== null;

  /*
   * UNA CLASE O LA OTRA, NUNCA LAS DOS.
   *
   * Poniéndole las dos, cuál opacidad gana depende del orden en que estén
   * escritas en el archivo de estilos —son dos selectores de una clase, misma
   * especificidad— y mover una regla de lugar cambiaría cómo se ve esto.
   */
  const clase = concretado ? ' post-settled' : post.past ? ' post-past' : '';

  return (
    <li className={`card post-card${clase}`}>
      {/*
        LA FOTO Y EL NOMBRE LLEVAN A LA FICHA.

        Javier: «haz que pueda ver el perfil de la persona que pide el reto».
        Antes de escribirle por WhatsApp uno quiere saber quién es —qué
        categoría juega, dónde, cómo le fue— y eso ya vive en su ficha.

        UN SOLO ENLACE ALREDEDOR DE LOS DOS y no uno por cosa: la foto y el
        nombre son la misma respuesta a «¿quién publicó esto?», y dos enlaces
        pegados que van al mismo lado se leen dos veces en un lector de
        pantalla y dan dos blancos diminutos en el teléfono en vez de uno
        cómodo.

        LA FECHA QUEDA AFUERA: no es parte de quién es, y meterla adentro
        haría que el enlace abarque un renglón que no lleva a ningún lado.
      */}
      <div className="post-head">
        <Link className="post-person" to={`/jugadores/${post.createdById}`}>
          <Avatar url={assetUrl(post.createdByAvatarPath)} name={post.createdByName} size={40} />
          <strong>{post.createdByName}</strong>
        </Link>
        <span className="help">
          {fecha(post.playsOn)}
          {post.past && ` · ${t('posts.past')}`}
        </span>
      </div>

      {/*
        LA MARCA DE CONCRETADO VA ARRIBA DE TODO, antes del lugar: cambia lo
        que el aviso significa —ya no ofrece ese partido— y quien lo lee tiene
        que saberlo antes de leer el resto, no después.

        Y DICE PARA QUÉ SIGUE AHÍ. Un aviso tachado sin explicación parece un
        error; con la línea, es una invitación a escribirle igual.
      */}
      {concretado && (
        <p className="post-settled-mark">
          <span className="tag tag-done">{t('posts.settled')}</span>{' '}
          <span className="help">{t('posts.settledHelp')}</span>
        </p>
      )}

      <p className="post-place">{post.placeText}</p>

      <p className="post-levels">
        {post.levels.map((level) => (
          <span className="tag tag-category" key={level}>
            {t(`categoryLevel.${level}`)}
          </span>
        ))}
      </p>

      {post.note !== null && <p className="help post-note">{post.note}</p>}

      {aviso !== null && <p className="error">{aviso}</p>}

      <div className="post-actions">
        {enlace === null ? (
          <button type="button" onClick={() => void escribir()} disabled={pidiendo}>
            {pidiendo ? t('common.loading') : t('posts.whatsapp')}
          </button>
        ) : (
          <a className="button-like whatsapp" href={enlace} target="_blank" rel="noreferrer noopener">
            {t('posts.whatsappOpen')}
          </a>
        )}

        {post.mine && (
          <>
            {/*
              CONCRETAR ES EL BOTÓN NORMAL Y BORRAR EL RARO, y por eso uno es
              botón y el otro un enlace chiquito. Lo normal es conseguir
              partido; borrar es para el aviso que no tenía que estar ahí.
            */}
            {concretado ? (
              <button type="button" className="link" onClick={() => onSettle(false)}>
                {t('posts.unsettle')}
              </button>
            ) : (
              <button type="button" className="secondary" onClick={() => onSettle(true)}>
                {t('posts.settle')}
              </button>
            )}
            <button type="button" className="link" onClick={onEdit}>
              {t('posts.edit')}
            </button>
            {/*
              BORRAR PIDE CONFIRMACIÓN, y en la misma tarjeta.
              Es el único botón de acá que no se puede deshacer: el aviso no
              queda en ningún lado. Un `confirm()` del navegador se ve distinto
              en cada teléfono y en algunos ni sale.
            */}
            {confirmando ? (
              <>
                <button type="button" className="link danger" onClick={onDelete}>
                  {t('posts.deleteSure')}
                </button>
                <button type="button" className="link" onClick={() => setConfirmando(false)}>
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <button type="button" className="link danger" onClick={() => setConfirmando(true)}>
                {t('posts.delete')}
              </button>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function PostForm({
  aviso,
  onDone,
  onCancel,
}: {
  aviso: ChallengePost | null;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [values, setValues] = useState<Formulario>(() =>
    aviso === null
      ? VACÍO
      : {
          playsOn: aviso.playsOn,
          placeText: aviso.placeText,
          levels: aviso.levels,
          note: aviso.note ?? '',
        },
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);

  function patch(cambio: Partial<Formulario>): void {
    setValues((previo) => ({ ...previo, ...cambio }));
  }

  function alternar(level: CategoryLevel, marcado: boolean): void {
    patch({
      levels: marcado ? [...values.levels, level] : values.levels.filter((l) => l !== level),
    });
  }

  async function guardar(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setEnviando(true);
    setError(null);
    setFieldErrors({});
    const cuerpo: SaveChallengePostRequest = {
      playsOn: values.playsOn,
      placeText: values.placeText,
      levels: values.levels,
      ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
    };
    try {
      if (aviso === null) await api.createPost(cuerpo);
      else await api.updatePost(aviso.id, cuerpo);
      onDone();
    } catch (caught) {
      // `errorSummary` y no `errorMessage`: los mensajes por campo se pintan al
      // lado de su input, y repetirlos arriba los dice dos veces.
      setError(errorSummary(t, caught));
      const detalle = (caught as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (detalle !== undefined) setFieldErrors(detalle);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <AppLayout>
      <form className="card" onSubmit={(event) => void guardar(event)}>
        <h1>{aviso === null ? t('posts.add') : t('posts.editTitle')}</h1>

        {error !== null && <Alert kind="error">{error}</Alert>}

        <Field id="post-date" label={t('posts.playsOn')} error={fieldErrors['playsOn']}>
          {(control) => (
            <input
              {...control}
              type="date"
              value={values.playsOn}
              onChange={(event) => patch({ playsOn: event.target.value })}
            />
          )}
        </Field>

        <Field
          id="post-place"
          label={t('posts.place')}
          help={t('posts.placeHelp')}
          error={fieldErrors['placeText']}
        >
          {(control) => (
            <input
              {...control}
              type="text"
              maxLength={CHALLENGE_POST_LIMITS.placeMax}
              value={values.placeText}
              onChange={(event) => patch({ placeText: event.target.value })}
            />
          )}
        </Field>

        <fieldset className="choice-group">
          <legend>{t('posts.levels')}</legend>
          <p className="help">{t('posts.levelsHelp')}</p>
          {CATEGORY_LEVELS.map((level) => (
            <div className="checkbox" key={level}>
              <input
                id={`post-level-${level}`}
                type="checkbox"
                checked={values.levels.includes(level)}
                onChange={(event) => alternar(level, event.target.checked)}
              />
              <label htmlFor={`post-level-${level}`}>{t(`categoryLevel.${level}`)}</label>
            </div>
          ))}
          {fieldErrors['levels'] !== undefined && (
            <span className="error">{fieldErrors['levels']}</span>
          )}
        </fieldset>

        <Field
          id="post-note"
          label={t('posts.note')}
          help={t('posts.noteHelp')}
          error={fieldErrors['note']}
        >
          {(control) => (
            <input
              {...control}
              type="text"
              maxLength={CHALLENGE_POST_LIMITS.noteMax}
              value={values.note}
              onChange={(event) => patch({ note: event.target.value })}
            />
          )}
        </Field>

        <div className="button-row">
          <button type="submit" disabled={enviando}>
            {t('common.save')}
          </button>
          <button type="button" className="secondary" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </AppLayout>
  );
}

/** «2026-09-20» tal como vino: es fecha civil y no lleva zona horaria. */
function fecha(iso: string): string {
  const [año, mes, día] = iso.split('-');
  return `${día}/${mes}/${año}`;
}

/**
 * EL MENSAJE QUE VA ESCRITO EN WHATSAPP, con los datos del aviso.
 *
 * Javier: «uno toca el whatsapp CON LOS DATOS DE PUBLICACIÓN y comienza a
 * conversar». Sin esto, quien recibe el mensaje no sabe de cuál de sus avisos
 * le están hablando, ni quien escribe se acuerda de contarlo.
 *
 * NO SE MANDA SOLO: `wa.me` lo deja escrito en la conversación para que la
 * persona lo lea, lo corrija y le dé enviar.
 */
function mensaje(t: TFunction, post: ChallengePost): string {
  return t('posts.whatsappText', {
    date: fecha(post.playsOn),
    place: post.placeText,
  });
}

/**
 * EL ATAJO ELEGIDO A UN RANGO DE DÍAS, CALCULADO AL PEDIR.
 *
 * `undefined` en los dos extremos para «TODO», que es lo que el cliente
 * entiende como «sin filtrar por día».
 *
 * SE USA EL RELOJ DE QUIEN MIRA, a propósito: «hoy» es el día de quien está
 * parado en la cancha, no el del servidor. Los avisos se guardan como día
 * civil, así que se compara día contra día y la zona horaria no entra.
 */
function rangoDe(cuando: 'TODO' | 'HOY' | 'MAÑANA'): { from?: string; to?: string } {
  if (cuando === 'TODO') return {};
  const d = new Date();
  if (cuando === 'MAÑANA') d.setDate(d.getDate() + 1);
  const día = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
  // UN SOLO DÍA: desde y hasta el mismo. El servidor los toma inclusive.
  return { from: día, to: día };
}

/**
 * LA CAJA DE FILTROS.
 *
 * DOS CHECKS Y UNA FILA DE CATEGORÍAS, y nada más. Javier pidió los checks de
 * hoy y mañana por nombre; las categorías salen de que cada aviso ya las
 * lleva. Un tercer eje —el lugar— sería una búsqueda de texto con sus propios
 * problemas, y esto tiene que caber arriba de la lista sin taparla.
 *
 * TODO ACÁ ES DE A UNO, y los tres ejes se dibujan igual.
 *
 * «HOY» Y «MAÑANA» SE EXCLUYEN porque el servidor recibe un desde y un hasta,
 * no una lista de días: marcar los dos sería un tercer rango y no la suma.
 *
 * LA CATEGORÍA TAMBIÉN, y esto Javier lo pidió mirándolo andar: «si selecciono
 * B y luego C mezcla los dos». Antes se podían marcar varias y traía los
 * avisos de B O de C, que es lo que él leyó como «mezclados» — la lista salía
 * revuelta sin que nada dijera por qué. Ahora marcar C desmarca B, y volver a
 * tocar la marcada la quita, que es lo que un check tiene que hacer.
 *
 * SE DIBUJAN COMO CAJITAS Y NO COMO CHIPS APRETADOS. El chip pintado no decía
 * si se podía marcar más de uno, y era justo la pregunta que el filtro estaba
 * contestando mal. Con las cajitas se ve de una que hay una sola marcada.
 */
function Filtros({
  niveles,
  cuando,
  onNiveles,
  onCuando,
}: {
  niveles: CategoryLevel[];
  cuando: 'TODO' | 'HOY' | 'MAÑANA';
  onNiveles: (siguiente: CategoryLevel[]) => void;
  onCuando: (siguiente: 'TODO' | 'HOY' | 'MAÑANA') => void;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="post-filters">
      <div className="post-when">
        {(['HOY', 'MAÑANA'] as const).map((opción) => (
          <label key={opción} className="post-check">
            <input
              type="checkbox"
              checked={cuando === opción}
              // Marcar uno apaga el otro; volver a tocarlo vuelve a «todo».
              onChange={() => onCuando(cuando === opción ? 'TODO' : opción)}
            />
            {t(opción === 'HOY' ? 'posts.today' : 'posts.tomorrow')}
          </label>
        ))}
      </div>

      <div className="post-level-filter">
        {CATEGORY_LEVELS.map((level) => {
          const puesto = niveles.includes(level);
          return (
            <label key={level} className="post-check post-level-check">
              <input
                type="checkbox"
                checked={puesto}
                /*
                 * MARCAR UNA DESMARCA LA OTRA: se manda la lista con esta sola,
                 * no esta agregada a lo que ya había. Volver a tocar la marcada
                 * vuelve a «todas».
                 */
                onChange={() => onNiveles(puesto ? [] : [level])}
              />
              {t(`categoryLevel.${level}`)}
            </label>
          );
        })}
      </div>
    </div>
  );
}
